import { useQuery } from '@tanstack/react-query';
import {
  PkAccordionItem,
  PkAccordionItemContent,
  PkAccordionItemTitle,
  PkCollapse,
  PkIcon,
} from '@uefadigital/panenka-react';
import type { ColDef, GetRowIdParams } from 'ag-grid-community';
import useThrottledCallback from 'beautiful-react-hooks/useThrottledCallback';
import useWindowScroll from 'beautiful-react-hooks/useWindowScroll';
import clsx from 'clsx';
import parseHtmlToReact from 'html-react-parser';
import type { CSSProperties } from 'react';
import { lazy, useMemo, memo, useCallback, useState } from 'react';

import { tableGroupsConfig } from './configuration';
import Legend from './Legend';
import TablePlace from './TablePlace';

import type { MatchApiParams } from '@apis/matches';
import { useWindowSize } from '@hooks/useWindowSize';
import { getMatchesQuery } from '@queries/matches';
import { hydrateWithTranslations } from '@services/TranslatorService';
import DataTableGrid from '@shared/DataTableGrid';
import TeamCellRender from '@shared/DataTableGrid/components/TeamCellRender';
import LiveIndicator from '@shared/LiveIndicator';
import QualifiedIndicator from '@shared/QualifiedIndicator';
import { useTranslateFn } from '@shared/Translate';
import { withSuspense } from '@shared/withSuspense';
import { Order } from '@shared-types/common.types';
import type { Match } from '@shared-types/match.types';
import { MatchStatus } from '@shared-types/match.types';
import type { StandingsOptions, GroupStanding, Standing } from '@shared-types/standing.types';
import { StandingStatus } from '@shared-types/standing.types';
import GoogleTagManagerHelper from '@tracking/gtm-helper';
import { compareDates } from '@utilities/match-sorting';

import * as classes from '../styles.module.scss';

const StandingsMatches = lazy(() => import(/* webpackPrefetch: true */ '../StandingsMatches'));
const FormGuideCellRender = lazy(
  () => import(/* webpackPrefetch: true */ '@shared/DataTableGrid/components/FormGuideCellRender'),
);

const getRowId = (row: GetRowIdParams<Standing>) => row.data?.team?.id;

export const StandingTable = ({
  cup,
  season,
  isDomestic,
  groupStanding: { group, items: standings, status, qualificationLabels },
  columns,
  enableFormguide,
  enableFormguidePopover,
  enableMatches,
  enableSorting,
  enableTeamLink = true,
  teamLinkTarget = '_self',
  newTableFormat,
  splitTablePlaces,
  procedureUrl,
  procedureUrlText,
  removeHeading,
  enableLegend,
  enableCollapse,
  teamIds,
  tracking,
  hideQualified,
  enableRanking,
  onRowHover,
  enableTrend,
}: StandingsOptions & { groupStanding: GroupStanding }) => {
  const t = useTranslateFn();
  const { isMobile } = useWindowSize();
  const onScroll = useWindowScroll();
  const [headerHeight, setHeaderHeight] = useState<number>(0);
  const [matchStreamVideoHeight, setMatchStreamVideoHeight] = useState<number>(0);

  onScroll(
    useThrottledCallback(
      () => {
        const MatchStreamVideoElem = document.querySelector('.match-stream__container--affix');
        setMatchStreamVideoHeight(MatchStreamVideoElem?.clientHeight ?? 0);

        const headerElem = document.querySelector('.js-standings--header-only');
        setHeaderHeight(headerElem?.clientHeight ?? 0);
      },
      [],
      100,
    ),
  );

  const filters = useMemo<MatchApiParams>(
    () => ({
      isDomestic,
      competitionId: cup,
      seasonYear: season,
      status: group?.id ? null : MatchStatus.FINISHED,
      groupId: group?.id,
      order: Order.ASC,
      limit: 500,
      offset: 0,
    }),
    [cup, group?.id, isDomestic, season],
  );

  const { data: matches } = useQuery({
    ...getMatchesQuery(filters),
    enabled: !!enableMatches || !!enableFormguide,
  });

  const getNextMatchByTeam = useCallback(
    (teamId: string) =>
      matches.find(
        (m: Match) =>
          [MatchStatus.LIVE, MatchStatus.UPCOMING].includes(m.status) &&
          [m.homeTeam?.id, m.awayTeam?.id].includes(teamId),
      ),
    [matches],
  );

  const getMatchesByTeam = useCallback(
    (teamId: string) =>
      matches
        .filter(
          (m: Match) =>
            [MatchStatus.FINISHED, MatchStatus.ABANDONED].includes(m.status) &&
            [m.homeTeam?.id, m.awayTeam?.id].includes(teamId),
        )
        .toSorted((a, b) => compareDates(b.kickOffTime?.dateTime, a.kickOffTime?.dateTime))
        .slice(0, 5),
    [matches],
  );

  const showRank = useMemo(() => {
    const firstMatch = matches.find((m) => m?.matchday?.name === 'MD1');

    return enableRanking ?? (!!firstMatch && ![MatchStatus.UNKNOWN, MatchStatus.UPCOMING].includes(firstMatch?.status));
  }, [matches, enableRanking]);

  const handleAction = (
    event: CustomEvent<{
      expanded: boolean;
    }>,
  ) => {
    const event_action = event.detail.expanded ? 'Expand - League phase table' : 'Collapse - League phase table';
    const event_label = 'standings';

    GoogleTagManagerHelper.event(
      'Click',
      event_action,
      event_label,
      { section_reference: event_label, interaction: event_action },
      event.detail.expanded ? 'table_expand' : 'table_collapse',
    );
  };

  const handleTeamOpen = useCallback(
    (e: CustomEvent) => {
      if (!tracking) return;
      const data = { ...tracking, variant: 'open team' };
      GoogleTagManagerHelper.gtmCardElementClick(e.target as HTMLElement, data);
    },
    [tracking],
  );

  const rowData = useMemo(
    () =>
      standings?.map((stat) => ({
        ...stat,
        team: {
          ...stat.team,
          nextMatch: getNextMatchByTeam(stat.team.id),
        },
        formguide: {
          matches: getMatchesByTeam(stat.team.id),
          teamId: stat.team.id,
        },
      })),
    [standings, getNextMatchByTeam, getMatchesByTeam],
  );

  const HeaderLiveIndicator = useCallback(
    () => status === StandingStatus.LIVE && <LiveIndicator className={classes.liveLabel} size='S' label='live_now' />,
    [status],
  );

  const columnTypes = useMemo<Record<string, ColDef>>(
    () => ({
      team: {
        cellClass: 'ag-cell--uefa_team',
        cellDataType: 'team',
        cellRenderer: TeamCellRender,
        resizable: false,
        sortable: false,
        suppressMovable: true,
        headerComponent: HeaderLiveIndicator,
        cellRendererParams: {
          onClick: handleTeamOpen,
          showRank,
          hideQualified,
          showTrend: enableTrend,
          enableLink: enableTeamLink,
          linkTarget: teamLinkTarget,
        },
      },
      formguide: {
        cellClass: 'ag-cell--uefa_formguide',
        cellDataType: 'formguide',
        cellRenderer: withSuspense(FormGuideCellRender),
        cellRendererParams: {
          disablePopover: !enableFormguidePopover,
        },
        resizable: false,
        sortable: false,
        hide: !enableFormguide,
      },
    }),
    [
      HeaderLiveIndicator,
      handleTeamOpen,
      showRank,
      hideQualified,
      enableTrend,
      enableTeamLink,
      teamLinkTarget,
      enableFormguidePopover,
      enableFormguide,
    ],
  );

  const someQualified = useMemo(() => standings.some((s) => s.qualified || !!s.qualificationLabelId), [standings]);

  const handleDetailToggle = (event: CustomEvent<{ expanded: boolean }>) => {
    if (!event.detail.expanded || !group.metaData) return;

    GoogleTagManagerHelper.event(
      null,
      null,
      null,
      { interaction: `Expand group matches - ${group.metaData.groupName}`, section_reference: 'group matches' },
      'table_expand',
    );
  };

  const commonProps = useMemo(
    () => ({
      columnDefs: columns,
      columnTypes: columnTypes,
      sortable: enableSorting,
      suppressRowTransform: enableFormguide,
      splitTablePlaces: splitTablePlaces,
      getRowId: getRowId,
      rowHeight: 65,
      onRowHover,
    }),
    [columnTypes, columns, enableFormguide, enableSorting, onRowHover, splitTablePlaces],
  );

  const dataGroups = useMemo(
    () =>
      tableGroupsConfig.map((group) => ({
        id: group.id,
        props: {
          placeTitle: group.placeTitle,
          placeSubTitle: group.placeSubTitle,
          wrapperClassName: group.wrapperClassName,
          rowData: rowData.slice(group.start, group.end),
        },
      })),
    [rowData],
  );

  return (
    <>
      <div
        className={clsx(classes.standings, enableMatches && classes.standingsWithCollapse)}
        style={
          {
            '--_match-stream--video-height': isMobile && `${matchStreamVideoHeight}px`,
            '--_header-height': `${headerHeight}px`,
          } as CSSProperties
        }
      >
        {group && (
          <div
            className={`pk-d--flex pk-gap--s pk-mb--${removeHeading ? 'xs' : 'l'} pk-font--base-semibold pk-line-height--md`}
          >
            {!removeHeading && (
              <h2 className='pk-text--text-01 pk-font-size--l pk-font-size-sm--xl'>
                {newTableFormat ? t('league_phase_table') : (hydrateWithTranslations(group)?.name ?? 'group')}
              </h2>
            )}
          </div>
        )}

        {!newTableFormat ? (
          <DataTableGrid {...commonProps} rowData={rowData} />
        ) : (
          <>
            <div className={clsx(splitTablePlaces && classes.newFormatPlaces)}>
              <TablePlace
                {...commonProps}
                wrapperClassName={clsx(classes.newFormatHeaderOnly, 'js-standings--header-only')}
                rowData={[]}
                onlyHeader
                teamIds={teamIds}
              />
              {enableCollapse ? (
                <>
                  <TablePlace {...commonProps} {...dataGroups[0].props} headerHeight={0} teamIds={teamIds} />
                  <PkCollapse
                    position='left'
                    iconName='navigation-chevron-down'
                    expandedLabel={t('close_full_table')}
                    collapsedLabel={t('see_full_table')}
                    onPkChange={handleAction}
                  >
                    <div slot='content'>
                      {dataGroups.slice(1).map((group) => (
                        <TablePlace
                          key={group.id}
                          {...commonProps}
                          {...group.props}
                          headerHeight={0}
                          teamIds={teamIds}
                        />
                      ))}
                    </div>
                  </PkCollapse>
                </>
              ) : (
                <>
                  {dataGroups.map((group) => (
                    <TablePlace key={group.id} {...commonProps} {...group.props} headerHeight={0} teamIds={teamIds} />
                  ))}
                </>
              )}
            </div>
            {enableLegend && <Legend someQualified={someQualified} labels={qualificationLabels} />}
            {!!procedureUrl && (
              <div className='pk-text--text-02 pk-font-size--xs pk-font-size-sm--s pk-mt--m'>
                {parseHtmlToReact(t(procedureUrlText, { replace: { url: procedureUrl } }))}
              </div>
            )}
          </>
        )}
      </div>

      {enableMatches && !!matches?.length && (
        <PkAccordionItem className={classes.accordion} hasDivider={false} onPkToggle={handleDetailToggle}>
          <PkAccordionItemTitle className={classes.accordionTitle} hideIcon>
            <div className='pk-d--flex pk-align-items--center pk-text--interaction'>
              {t('group_details')}
              <PkIcon
                color='interaction'
                name='navigation-chevron-down'
                className={clsx(classes.accordionIcon, 'pk-ml--xs2')}
              />
            </div>
            {someQualified && (
              <div
                slot='title-right-image'
                className='pk-d--flex pk-align-items--center pk-gap--xs pk-font-size--s pk-text--text-02'
              >
                <QualifiedIndicator type='dot' />
                {t('qualified')}
              </div>
            )}
          </PkAccordionItemTitle>
          <PkAccordionItemContent className={classes.accordionContent}>
            <StandingsMatches matches={matches} />
          </PkAccordionItemContent>
        </PkAccordionItem>
      )}
    </>
  );
};

export default memo(StandingTable);
